import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CreditCard, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSiteContent } from "@/context/SiteContentContext";
import { getIcon } from "@/lib/icons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const BENEFITS = [
  { iconKey: "crmb1_icon", titleKey: "crmb1_title", descKey: "crmb1_desc" },
  { iconKey: "crmb2_icon", titleKey: "crmb2_title", descKey: "crmb2_desc" },
  { iconKey: "crmb3_icon", titleKey: "crmb3_title", descKey: "crmb3_desc" },
  { iconKey: "crmb4_icon", titleKey: "crmb4_title", descKey: "crmb4_desc" },
];

export const CrmService = () => {
  const { t } = useSiteContent();
  const [loading, setLoading] = useState(false);

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout/session`, {
        plan_id: "crm",
        origin_url: window.location.origin,
      });
      if (data.url) window.location.href = data.url;
      else throw new Error("No checkout URL");
    } catch (err) {
      toast.error("No se pudo iniciar el pago. Inténtalo de nuevo.");
      setLoading(false);
    }
  };

  const rawPrice = (t('crm_price') || "").trim();
  const isNum = /^\d+([.,]\d+)?$/.test(rawPrice);

  return (
    <section id="crm" className="relative py-28 px-6 bg-gradient-to-b from-transparent via-[#0a0a12] to-transparent" data-testid="crm-section">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto"
        >
          <span className="text-sm uppercase tracking-[0.2em] text-indigo-400 font-medium">Nuevo servicio</span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            {t('crm_section_title')} <span className="text-gradient">{t('crm_section_highlight')}</span>
          </h2>
          <p className="mt-4 text-white/55 text-lg">{t('crm_section_subtitle')}</p>
        </motion.div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {BENEFITS.map((b, i) => {
            const Icon = getIcon(t(b.iconKey));
            return (
              <motion.div
                key={b.titleKey}
                data-testid={`crm-benefit-${i}`}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
                className="glass rounded-2xl p-7 hover:border-white/20 transition-colors duration-300"
              >
                <span className="grid place-items-center h-12 w-12 rounded-xl bg-gradient-to-br from-indigo-500/20 to-violet-500/20 mb-5">
                  <Icon className="h-6 w-6 text-indigo-300" />
                </span>
                <h3 className="text-lg font-semibold mb-2">{t(b.titleKey)}</h3>
                <p className="text-white/55 text-sm leading-relaxed">{t(b.descKey)}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Dedicated CRM plan card */}
        <motion.div
          id="plan-crm"
          data-testid="crm-plan-card"
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.5 }}
          className="mt-14 max-w-xl mx-auto rounded-3xl p-8 border border-indigo-400/40 bg-gradient-to-b from-indigo-500/[0.1] to-violet-500/[0.04] shadow-[0_8px_40px_rgba(99,102,241,0.18)] scroll-mt-28"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-xl font-semibold">{t('crm_name')}</h3>
              <p className="text-white/55 text-sm mt-1">{t('crm_desc')}</p>
            </div>
            <div className="text-right shrink-0">
              <span className="text-3xl font-bold text-gradient">{isNum ? `${rawPrice}€` : rawPrice}</span>
              {isNum && <span className="text-white/50 text-sm">/mes</span>}
            </div>
          </div>
          <ul className="mt-6 flex flex-col gap-2.5">
            {BENEFITS.map((b) => (
              <li key={b.titleKey} className="flex items-center gap-2.5 text-sm text-white/75">
                <Check className="h-4 w-4 text-emerald-400 shrink-0" /> {t(b.titleKey)}
              </li>
            ))}
          </ul>
          <Button
            data-testid="crm-subscribe-button"
            onClick={handleCheckout}
            disabled={loading}
            className="mt-7 w-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-400 hover:to-violet-400 text-white h-12 gap-2 text-base border-0"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <CreditCard className="h-5 w-5" />}
            Suscribirme al CRM
          </Button>
          <button
            data-testid="crm-contact-link"
            onClick={() => scrollTo("contact")}
            className="mt-4 w-full text-center text-indigo-400 hover:text-indigo-300 text-sm font-medium transition-colors"
          >
            {t('crm_section_cta')} <ArrowRight className="inline h-4 w-4" />
          </button>
        </motion.div>
      </div>
    </section>
  );
};
